import { either } from 'fp-ts';
import { Either } from 'fp-ts/lib/Either';
import { SupportedListingEntities } from 'modules/Listings/types';
import { ReactLeft, UI_STATES } from 'utils/uiStates/uiStates';
import { useCurrentStore } from 'modules/Listings/store/useCurrentStore';
import { ScrollTrigger } from 'components/List/ScrollTrigger';
import { AllListStoreTypes } from 'modules/Listings/store/StateContext';
import { List } from 'components/List';
import { observer } from 'mobx-react-lite';
import { NoStarredTemplateResource } from 'components/NoAppState/NoStarredResource';
import { NoSearchState } from 'components/NoAppState/NoSearchState';
import { NoResourceState } from 'components/NoAppState/NoResourceState';
import { IconBox } from 'components/Icons';
import { NoAppCTA } from 'components/NoAppState/NoAppCTA';
import { noAppsTitle, noAppsTitleOwner, noAppsSubtitle, noAppsSubtitleOwner, noAppsSearchTitle, noAppsSearchSubtitle } from 'components/NoAppState/NoAppState.constants';
import { baseTheme } from 'styles/utils';
import { noAppSubText } from 'components/NoAppState/NoAppState.styles';
import { textRegularLight } from 'styles/typography';
import { cx } from '@linaria/core';
import { wrapper } from './ListView.styled';
export interface State {
  initialise: () => void;
  load: () => void;
  list: Either<ReactLeft, SupportedListingEntities[]>;
  query: string;
  makeUrl: (a: Record<string, string>) => string;
  overrideUrlClick: (entitiy: SupportedListingEntities) => void;
  getIsEntityOwner: (b: Record<string, string>) => boolean;
}
interface ListViewProps {
  pageName: string;
  parentWidth?: number;
  showTags?: boolean;
}
export const ListView: React.FC<ListViewProps> = observer(({
  pageName,
  parentWidth,
  showTags
}) => {
  const state = useCurrentStore();
  if (either.isLeft(state.list) && state.list.left.type === UI_STATES.loading) return <ScrollTrigger store={state} />;
  if (either.isLeft(state.list)) return getListViewEmptyState(state.searchTermValue, state.defaultParams);
  return <ListViewControlled pageName={pageName} state={state} parentWidth={parentWidth} showTags={showTags} />;
});
interface ListViewControlledProps {
  state: AllListStoreTypes;
  pageName: string;
  parentWidth?: number;
  showTags?: boolean;
}
export const ListViewControlled: React.FC<ListViewControlledProps> = ({
  state,
  pageName,
  parentWidth,
  showTags
}) => {
  if (!either.isRight(state.list)) return null;
  return <div className={wrapper}>
      <List parentWidth={parentWidth} list={state.list.right} pageName={pageName} makeUrl={state.makeUrl} getIsEntityOwner={state.getIsEntityOwner} overrideUrlClick={'overrideUrlClick' in state ? state.overrideUrlClick : undefined} showTags={showTags} />
    </div>;
};
export const testable = {
  ListViewInternal: ListView
};
const getListViewEmptyState = (searchTermValue?: string, defaultParams?: string) => {
  const isStarredOnly = defaultParams?.includes('&starred_only=true');
  const isTemplateOnly = defaultParams?.includes('&template_only=true');
  return isStarredOnly || isTemplateOnly ? <>
      {isStarredOnly && <NoStarredTemplateResource title="No starred apps" subtitle="Star any app to see it here" />}
      {isTemplateOnly && <NoStarredTemplateResource title="There are no templates yet." subtitle="Mark an App as a template and use it to create new Apps fast." isTemplate />}
    </> : searchTermValue ? <NoSearchState title={`${noAppsSearchTitle} "${searchTermValue}"`} subtitle={noAppsSearchSubtitle} /> : <NoResourceState isCtaVisible title={isOwner => isOwner ? noAppsTitleOwner : noAppsTitle} subtitle={isOwner => <h4 className={cx(textRegularLight, noAppSubText)}>{isOwner ? noAppsSubtitleOwner : noAppsSubtitle}</h4>} icon={<IconBox size={24} color={baseTheme.light.colors.blue} />} cta={<NoAppCTA />} />;
};